// src/components/onboarding/OwnerFormFields.tsx
//
// The "Owner Onboarding Form": a flat object with fixed keys, presented in
// visual groups (Owner info / Property / Legal execution / Tax / Bank details).
// Tax (ssn, ein) and bank fields are treated as sensitive and rendered as
// masked (password-style) inputs with a per-field reveal toggle.
//
// NOTE: field renderers are plain functions (invoked inline), NOT nested React
// components, so inputs are not remounted on each keystroke (avoids focus loss).

import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { OwnerForm } from '../../types';

interface OwnerFormFieldsProps {
    value: OwnerForm;
    onChange: (next: OwnerForm) => void;
}

const inputClass =
    'w-full px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50 focus:bg-white focus:border-brand focus:ring-2 focus:ring-brand/20 outline-none transition-all text-sm';

const groupClass = 'bg-white rounded-2xl border border-gray-200 shadow-sm p-6 md:p-8';

const OwnerFormFields: React.FC<OwnerFormFieldsProps> = ({ value, onChange }) => {
    const [revealed, setRevealed] = useState<Record<string, boolean>>({});

    const set = (key: keyof OwnerForm, v: string) => onChange({ ...value, [key]: v });
    const toggleReveal = (key: string) => setRevealed((r) => ({ ...r, [key]: !r[key] }));

    const isCompany = value.executionType === 'Company';

    const field = (
        name: keyof OwnerForm,
        label: string,
        opts: { type?: string; placeholder?: string; colSpan?: boolean } = {},
    ) => (
        <div key={name} className={opts.colSpan ? 'md:col-span-2' : ''}>
            <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
            <input
                type={opts.type || 'text'}
                value={value[name]}
                onChange={(e) => set(name, e.target.value)}
                placeholder={opts.placeholder}
                className={inputClass}
            />
        </div>
    );

    const sensitive = (name: keyof OwnerForm, label: string, placeholder?: string) => {
        const shown = !!revealed[name];
        return (
            <div key={name}>
                <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">{label}</label>
                <div className="relative">
                    <input
                        type={shown ? 'text' : 'password'}
                        value={value[name]}
                        onChange={(e) => set(name, e.target.value)}
                        placeholder={placeholder}
                        autoComplete="off"
                        className={`${inputClass} pr-11`}
                    />
                    <button
                        type="button"
                        onClick={() => toggleReveal(name)}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-brand transition-colors"
                        tabIndex={-1}
                        aria-label={shown ? 'Hide value' : 'Show value'}
                    >
                        {shown ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                </div>
            </div>
        );
    };

    return (
        <div className="space-y-8">
            <section className={groupClass}>
                <h3 className="font-serif font-bold text-xl text-primary mb-4">Owner Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {field('ownerFullName', 'Owner full name', { colSpan: true })}
                    {field('dateOfBirth', 'Date of birth', { type: 'date' })}
                    {field('emailAddress', 'Email address', { type: 'email' })}
                    {field('contactPrimary', 'Primary contact number')}
                    {field('contactSecondary', 'Secondary contact number')}
                </div>
            </section>

            <section className={groupClass}>
                <h3 className="font-serif font-bold text-xl text-primary mb-4">Property &amp; Mailing</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {field('propertyAddress', 'Property address', { colSpan: true })}
                    {field('mailingAddress', 'Mailing address', { colSpan: true })}
                </div>
            </section>

            <section className={groupClass}>
                <h3 className="font-serif font-bold text-xl text-primary mb-4">Legal Execution</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    <div className="md:col-span-2">
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                            Execution type
                        </label>
                        <div className="flex gap-3">
                            {(['Individual', 'Company'] as const).map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => set('executionType', opt)}
                                    className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                                        value.executionType === opt
                                            ? 'bg-brand text-white border-brand shadow-sm'
                                            : 'bg-white text-gray-600 border-gray-300 hover:border-brand hover:text-brand'
                                    }`}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                    {isCompany && field('companyName', 'Company name')}
                    {isCompany && field('companyType', 'Company type', { placeholder: 'e.g. LLC, S-Corp' })}
                    {isCompany && field('companyAddress', 'Company address', { colSpan: true })}
                </div>
            </section>

            <section className={groupClass}>
                <h3 className="font-serif font-bold text-xl text-primary mb-1">Tax Information</h3>
                <p className="text-sm text-gray-500 mb-5">Sensitive. These values are encrypted in transit and stored securely.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    {sensitive('ssn', 'SSN', '•••-••-••••')}
                    {sensitive('ein', 'EIN', '••-•••••••')}
                </div>
            </section>

            <section className={groupClass}>
                <h3 className="font-serif font-bold text-xl text-primary mb-1">Bank Details</h3>
                <p className="text-sm text-gray-500 mb-5">Sensitive. Used only to set up owner payouts.</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
                    <div>
                        <label className="block text-xs font-bold uppercase tracking-wider text-gray-500 mb-1.5">
                            Bank account type
                        </label>
                        <div className="flex gap-3">
                            {(['Checking', 'Saving'] as const).map((opt) => (
                                <button
                                    key={opt}
                                    type="button"
                                    onClick={() => set('bankAccountType', opt)}
                                    className={`px-5 py-2.5 rounded-xl text-sm font-bold border transition-all ${
                                        value.bankAccountType === opt
                                            ? 'bg-brand text-white border-brand shadow-sm'
                                            : 'bg-white text-gray-600 border-gray-300 hover:border-brand hover:text-brand'
                                    }`}
                                >
                                    {opt}
                                </button>
                            ))}
                        </div>
                    </div>
                    {field('bankName', 'Bank name')}
                    {sensitive('bankAccountNumber', 'Account number')}
                    {sensitive('bankRoutingNumber', 'Routing number')}
                    {field('bankAddress', 'Bank address', { colSpan: true })}
                </div>
            </section>
        </div>
    );
};

export default OwnerFormFields;
